/**
 * A custom element that formats rte content for easier styling
 */

const VOID_SELECTOR =
  'img, picture, video, iframe, svg, object, embed, canvas, input, textarea, select, table';

/**
 * True when the paragraph has no visible text and no meaningful embedded content.
 * Covers: <p></p>, <p> </p>, <p>&nbsp;</p>, <p><br></p>, <p><br><br></p>, etc.
 * @param {HTMLParagraphElement} p
 * @returns {boolean}
 */
function isEmptyRteParagraph(p) {
  if (!(p instanceof HTMLParagraphElement)) return false;
  if (p.closest('table')) return false;
  if (p.matches(VOID_SELECTOR) || p.querySelector(VOID_SELECTOR)) return false;
  const text = p.textContent
    .replace(/\u00a0/g, ' ')
    .replace(/\uFEFF/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return text === '';
}

/**
 * @param {ParentNode} container
 */
function processRteContainer(container) {
  const paragraphs = container.querySelectorAll('p');
  paragraphs.forEach((p) => {
    p.hidden = isEmptyRteParagraph(p);
    p.style.marginBlockEnd = '';
  });

  for (let i = paragraphs.length - 1; i >= 0; i -= 1) {
    const paragraph = paragraphs[i];
    if (paragraph && !paragraph.hidden) {
      paragraph.style.marginBlockEnd = '0';
      break;
    }
  }
}

/**
 * RTE blocks that are not handled by <rte-formatter> children (avoids double-processing).
 */
function initGlobalRteEmptyParagraphs() {
  document.querySelectorAll('.rte:not(:has(rte-formatter)), .shopify-policy__title').forEach(processRteContainer);
}

class RTEFormatter extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    this.querySelectorAll('table').forEach(this.#formatTable);
    this.#processParagraphs();
    this.#observer = new MutationObserver(() => {
      requestAnimationFrame(() => this.#processParagraphs());
    });
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  #processParagraphs() {
    processRteContainer(this);
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGlobalRteEmptyParagraphs, { once: true });
} else {
  initGlobalRteEmptyParagraphs();
}
